import React from 'react';
import {Tooltip} from 'react-tooltip';

import {ApiForecastThresholdAlertConfig} from 'src/lib/api/alert_configs';
import {Dims, Margin} from 'src/lib/commonTypes';
import {THRESHOLD_PLOT_COLORS} from 'src/lib/thresholdPlotColors';

import {YScale} from './D3Axis';
import {useTranslations} from './TranslationsContext';

type Props = {
  y: YScale;
  graphDims: Dims;
  thresholdConfigs: ApiForecastThresholdAlertConfig[];
};

export function ThresholdLines({graphDims, y, thresholdConfigs}: Props) {
  if (!thresholdConfigs || thresholdConfigs.length === 0) {
    return null;
  }

  return (
    <g className="sharp">
      {thresholdConfigs.map((config, index) => {
        const threshold = config.properties.threshold;
        const yPos = y(threshold);

        if (yPos > graphDims[1] || yPos < 0) {
          return null;
        }

        const color =
          config.properties.plotColor ||
          THRESHOLD_PLOT_COLORS[index % THRESHOLD_PLOT_COLORS.length];
        const label = config.properties.label || `${threshold} ${config.units || ''}`.trim();

        return (
          <g key={config.id}>
            <line
              x1={0}
              x2={graphDims[0]}
              y1={yPos}
              y2={yPos}
              stroke={color}
              strokeOpacity={0.6}
              strokeDasharray="6 3"
            />
            <text
              fontSize={10}
              x={graphDims[0] - 4}
              y={yPos}
              textAnchor="end"
              dominantBaseline="middle"
              fill={color}
              stroke="white"
              strokeWidth={4}
              strokeLinejoin="round"
              fontWeight={500}
              style={{paintOrder: 'stroke fill'}}
            >
              {label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

type OverlayProps = {
  y: YScale;
  graphDims: Dims;
  margin: Margin;
  thresholdConfigs: ApiForecastThresholdAlertConfig[];
};

function ThresholdTooltipContent({config}: {config: ApiForecastThresholdAlertConfig}) {
  const {t} = useTranslations();
  const {source, column, direction, threshold, rateVolumeMode, label} = config.properties;
  const dir = direction === 'above' ? '>' : '<';
  const units = config.units || '';

  return (
    <>
      {label && (
        <div>
          <b>{label}</b>
        </div>
      )}
      <div>
        <b>{t('thresholdCreatedByLabel')}</b> {config.createdByName}
      </div>
      <div>
        <b>{t('thresholdSourceLabel')}</b> {source}
      </div>
      <div>
        <b>{t('thresholdColumnLabel')}</b> {column}
      </div>
      <div>
        <b>{t('thresholdModeLabel')}</b> {rateVolumeMode === 'level' ? t('waterLevel') : t('flow')}
      </div>
      <div>
        <b>{t('thresholdConditionLabel')}</b> {`${dir} ${threshold} ${units}`.trim()}
      </div>
    </>
  );
}

export function ThresholdLabelOverlays({y, graphDims, margin, thresholdConfigs}: OverlayProps) {
  if (!thresholdConfigs || thresholdConfigs.length === 0) {
    return null;
  }

  return (
    <>
      {thresholdConfigs.map((config) => {
        const threshold = config.properties.threshold;
        const yPos = y(threshold);

        if (yPos > graphDims[1] || yPos < 0) {
          return null;
        }

        const tooltipId = `threshold-${config.id}`;

        return (
          <React.Fragment key={config.id}>
            <div
              data-tooltip-id={tooltipId}
              style={{
                position: 'absolute',
                right: margin.right,
                top: margin.top + yPos - 8,
                width: 150,
                height: 16,
                cursor: 'pointer',
                zIndex: 10,
              }}
            />
            <Tooltip
              id={tooltipId}
              float
              className="!rounded !shadow-sm !border !border-gray-300 !p-1.5 !bg-gray-50 !text-gray-900 !text-[10px] !z-50"
            >
              <ThresholdTooltipContent config={config} />
            </Tooltip>
          </React.Fragment>
        );
      })}
    </>
  );
}
